import { useState } from "react";
import { ArrowRight, FileDown } from "lucide-react";
import { track } from "@/lib/analytics";
import LeadMagnetDialog from "./LeadMagnetDialog";

const interestChips: { label: string; target: string }[] = [
  { label: "What's reviewed", target: "#what-we-review" },
  { label: "Who it's for", target: "#who-its-for" },
  { label: "What you get", target: "#what-you-get" },
  { label: "How it works", target: "#how-it-works" },
];

const CheckupHero = () => {
  const [magnetOpen, setMagnetOpen] = useState(false);

  const scrollTo = (selector: string) => {
    document.querySelector(selector)?.scrollIntoView({ behavior: "smooth" });
  };

  const onPrimary = () => {
    track("hero_primary_cta_clicked", { label: "Request My Growth Checkup" });
    scrollTo("#checkup-form");
  };

  const onSecondary = () => {
    track("hero_secondary_cta_clicked", { label: "What we'll review (60-sec preview)" });
    scrollTo("#what-we-review");
  };

  const onDownload = () => {
    track("hero_download_checklist_clicked", { label: "Download the New Patient Leak Checklist" });
    setMagnetOpen(true);
  };

  const onChip = (label: string, target: string) => {
    track("hero_chip_clicked", { label, target });
    scrollTo(target);
  };

  return (
    <section className="relative bg-hero-bg overflow-hidden">
      <div className="relative z-10 max-w-4xl mx-auto section-padding pt-24 pb-14 md:pt-32 md:pb-18">
        <div className="text-center">
          <p className="text-xs font-bold tracking-[0.2em] uppercase text-accent mb-5 reveal-up">
            For $1M+ dental practice owners who want their life back
          </p>

          <h1 className="text-3xl md:text-5xl lg:text-[3.25rem] font-bold text-hero-foreground leading-[1.1] tracking-tight mb-5 reveal-up-delay-1">
            Build a Practice That Doesn't<br className="hidden md:block" /> Require All of You
          </h1>

          <p className="text-base md:text-lg text-hero-foreground/70 max-w-2xl mx-auto mb-4 leading-relaxed reveal-up-delay-1">
            Get a No-Touch Growth Score plus a 90-day plan that pinpoints the 2–3 things creating the most drag in your practice — so it can grow without constant attention from you.
          </p>

          <p className="text-sm text-hero-foreground/50 max-w-xl mx-auto mb-6 reveal-up-delay-2">
            No call. No pitch. Just your score and a plan — delivered in ~3 business days.
          </p>

          {/* Interest chips — passive engagement signals */}
          <div className="flex flex-wrap items-center justify-center gap-2 mb-8 reveal-up-delay-2">
            {interestChips.map((chip) => (
              <button
                key={chip.label}
                onClick={() => onChip(chip.label, chip.target)}
                className="text-xs font-medium text-hero-foreground/70 hover:text-hero-foreground bg-hero-foreground/5 hover:bg-hero-foreground/10 border border-hero-foreground/10 hover:border-hero-foreground/20 rounded-full px-3 py-1.5 transition-colors"
              >
                {chip.label}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 reveal-up-delay-2">
            <button
              onClick={onPrimary}
              className="inline-flex items-center justify-center gap-2 bg-accent text-accent-foreground px-8 py-4 rounded-lg text-base font-semibold hover:bg-accent/90 transition-all active:scale-[0.97] shadow-lg shadow-accent/25"
            >
              Get My Growth Score + 90-Day Plan
              <ArrowRight className="w-4 h-4" />
            </button>
            <button
              onClick={onSecondary}
              className="inline-flex items-center justify-center gap-2 text-hero-foreground/60 hover:text-hero-foreground px-6 py-4 rounded-lg text-sm font-medium transition-colors"
            >
              See what's reviewed (60-sec preview)
            </button>
          </div>

          {/* Free download — low-friction lead magnet */}
          <div className="mt-5 reveal-up-delay-2">
            <button
              onClick={onDownload}
              className="inline-flex items-center justify-center gap-2 text-hero-foreground bg-hero-foreground/5 hover:bg-hero-foreground/10 border border-hero-foreground/15 hover:border-hero-foreground/25 px-5 py-2.5 rounded-lg text-sm font-medium transition-colors"
            >
              <FileDown className="w-4 h-4" />
              Free: Download the New Patient Leak Checklist (PDF)
            </button>
          </div>

          {/* Guarantee microcopy — restates Ad #8 promise */}
          <p className="text-hero-foreground/60 text-xs sm:text-sm mt-4 reveal-up-delay-2">
            No call. No pitch. No obligation. Just your Growth Score + 90-day plan — delivered in ~3 business days.
          </p>

          {/* Trust strip */}
          <p className="text-hero-foreground/30 text-xs mt-6 reveal-up-delay-2">
            Dental-focused marketing experience · Hundreds of practices supported · Websites, ads, direct mail & tracking built to work together
          </p>
        </div>
      </div>
      <LeadMagnetDialog open={magnetOpen} onOpenChange={setMagnetOpen} redirectTo="/quick-score" source="hero-cta" />
    </section>
  );
};

export default CheckupHero;
